import { Check } from 'lucide-react'
import type { JourneyStep } from '@/api/journey'

/**
 * The journey, shaded from --gr-drkblue to --gr-drkgreen by step index (plan
 * §11): a finished segment blends into the next step's colour, unreached steps
 * use --color-border, labels stay --color-text.
 */
function shade(i: number, n: number): string {
  const pct = n <= 1 ? 100 : Math.round(100 - (100 * i) / (n - 1))
  return `color-mix(in srgb, var(--gr-drkblue) ${pct}%, var(--gr-drkgreen))`
}

export default function JourneyBar({ steps }: { steps: JourneyStep[] }) {
  const n = steps.length
  return (
    <div className="swb-card" style={{ padding: '16px 20px', overflowX: 'auto' }}>
      <ol className="swb-journey" aria-label="Your progress">
        {steps.map((st, i) => {
          const color = shade(i, n)
          const next = steps[i + 1]
          const line = st.state === 'done'
            ? `linear-gradient(90deg, ${color}, ${next ? shade(i + 1, n) : color})`
            : 'var(--color-border)'
          return (
            <li key={st.id} aria-current={st.state === 'current' ? 'step' : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                {st.state === 'done' ? (
                  <span className="swb-node" style={{ background: color, color: '#ffffff' }} aria-label="done">
                    <Check size={15} strokeWidth={3} />
                  </span>
                ) : st.state === 'current' ? (
                  <span className="swb-node" style={{ border: `2px solid ${color}`, background: 'var(--color-accent-weak)', color: 'var(--color-text)' }}>
                    {i + 1}
                  </span>
                ) : (
                  <span className="swb-node" style={{ border: '2px solid var(--color-border-strong)', color: 'var(--color-text-secondary)' }}>
                    {i + 1}
                  </span>
                )}
                {i < n - 1 && <span aria-hidden="true" className="swb-line" style={{ background: line }} />}
              </div>
              <span style={{ fontSize: 14, fontWeight: st.state === 'current' ? 600 : 500, color: 'var(--color-text)' }}>
                {st.label}{st.skipped ? ' (skipped)' : ''}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
